// Analytics core — canon Phase 4 public behaviour. DOM-free; port-injected.
// Guarantee (canon §4): phone & precise geo never enter the queue/events — every
// event is run through the shared PII guard before enqueue, and is dropped if it
// would leak PII.
import { assertNoPIIInAnalyticsPayload } from '@fixgarden/shared/privacy';
import type {
  AnalyticsConfig,
  ApiClient,
  Clock,
  Envelope,
  EventName,
  Logger,
} from './types';
import { SDK_DEFAULTS } from './types';
import type { Identity } from './ids';
import type { EventQueue } from './queue';

export interface QuestionRef {
  id: string;
  step: number;
}
export interface AnswerRef {
  id: string;
}

export interface LeadContext {
  anonymous_id: string;
  session_id: string;
  quiz_attempt_id?: string;
}

export interface AnalyticsDeps {
  config: AnalyticsConfig;
  identity: Identity;
  queue: EventQueue;
  api: ApiClient;
  clock: Clock;
  logger: Logger;
  /** Pseudonymous session context (utm, device, metrica_client_id, yclid, referrer). No PII. */
  sessionContext: () => Record<string, unknown>;
}

export class Analytics {
  private readonly cfg: Required<
    Pick<AnalyticsConfig, 'schemaVersion'>
  > &
    AnalyticsConfig;

  constructor(private readonly deps: AnalyticsDeps) {
    this.cfg = { schemaVersion: deps.config.schemaVersion ?? SDK_DEFAULTS.schemaVersion, ...deps.config };
  }

  /** Load queued leftovers from a previous session and start periodic flush. */
  async start(): Promise<void> {
    await this.deps.queue.init();
  }

  startSession(): void {
    const ctx = this.deps.sessionContext();
    void this.deps.api.startSession({
      session_id: this.deps.identity.sessionId(),
      anonymous_id: this.deps.identity.anonymousId(),
      ...ctx,
    });
    if (!ctx.metrica_client_id) this.track('yandex_id_unavailable');
  }

  startQuizAttempt(): string {
    const existing = this.deps.identity.attemptId();
    if (existing) {
      void this.deps.api.patchAttempt(existing, { status: 'started' });
      this.track('quiz_resume');
      return existing;
    }
    const id = this.deps.identity.startAttempt();
    void this.deps.api.createAttempt({
      quiz_attempt_id: id,
      session_id: this.deps.identity.sessionId(),
      anonymous_id: this.deps.identity.anonymousId(),
      quiz_version: this.deps.config.quizVersion,
      interface_version: this.deps.config.interfaceVersion,
    });
    this.track('quiz_open');
    this.track('quiz_start');
    return id;
  }

  abandonQuizAttempt(lastStep: number): void {
    this.track('quiz_close', { step: lastStep });
    this.track('quiz_abandon', { step: lastStep });
    this.patchAttempt({ status: 'abandoned', last_step: lastStep });
  }

  completeQuizAttempt(lastStep: number): void {
    this.patchAttempt({ status: 'completed', last_step: lastStep, completed_at: new Date(this.deps.clock.now()).toISOString() });
  }

  patchAttempt(patch: { status?: string; last_step?: number; completed_at?: string }): void {
    const id = this.deps.identity.attemptId();
    if (id) void this.deps.api.patchAttempt(id, patch);
  }

  track(name: EventName, properties: Record<string, unknown> = {}, leadId?: string): void {
    const envelope = this.buildEnvelope(name, properties, leadId);
    try {
      assertNoPIIInAnalyticsPayload(envelope.properties);
      assertNoPIIInAnalyticsPayload(envelope.context);
    } catch {
      // Defensive: never leak PII even if a caller mistakenly passes it (canon §4).
      this.deps.logger.warn('[fg-analytics] event blocked — would leak PII:', name);
      return;
    }
    void this.deps.queue.enqueue(envelope);
    if (this.deps.config.debug) this.deps.logger.debug('[fg-analytics]', name, envelope.properties);
  }

  trackQuestionView(question: QuestionRef): void {
    this.track('question_view', { question_id: question.id, step: question.step });
  }

  trackAnswerSelect(question: QuestionRef, answer: AnswerRef): void {
    this.track('answer_select', { question_id: question.id, step: question.step, answer_id: answer.id });
  }

  trackAnswerDeselect(question: QuestionRef, answer: AnswerRef): void {
    this.track('answer_deselect', { question_id: question.id, step: question.step, answer_id: answer.id });
  }

  trackAnswerChange(question: QuestionRef, answer: AnswerRef): void {
    this.track('answer_change', { question_id: question.id, step: question.step, answer_id: answer.id });
  }

  trackQuestionNext(question: QuestionRef): void {
    this.track('question_next', { question_id: question.id, step: question.step });
    this.patchAttempt({ last_step: question.step });
  }

  trackQuestionBack(question: QuestionRef): void {
    this.track('question_back', { question_id: question.id, step: question.step });
  }

  trackQuestionSkip(question: QuestionRef, answer: AnswerRef): void {
    const properties = { question_id: question.id, step: question.step, answer_id: answer.id };
    this.track('question_skip', properties);
    this.track('skip_to_agronomist', properties);
  }

  trackLeadFormView(): void {
    this.track('phone_form_view');
  }

  trackPhoneSubmitSuccess(leadId?: string): void {
    // NO phone — only the pseudonymous lead_id link (canon rule 3).
    this.track('phone_submit_success', {}, leadId);
  }

  /** Pseudonymous identifiers used to link a PII lead without exposing PII to analytics. */
  leadContext(): LeadContext {
    const context: LeadContext = {
      anonymous_id: this.deps.identity.anonymousId(),
      session_id: this.deps.identity.sessionId(),
    };
    const attemptId = this.deps.identity.attemptId();
    if (attemptId) context.quiz_attempt_id = attemptId;
    return context;
  }

  flush(opts: { beacon?: boolean } = {}): Promise<void> {
    return this.deps.queue.flush(opts);
  }

  private buildEnvelope(
    name: EventName,
    properties: Record<string, unknown>,
    leadId?: string,
  ): Envelope {
    const envelope: Envelope = {
      schema_version: this.cfg.schemaVersion,
      event_id: this.deps.identity.newEventId(),
      event_name: name,
      event_timestamp: new Date(this.deps.clock.now()).toISOString(),
      anonymous_id: this.deps.identity.anonymousId(),
      session_id: this.deps.identity.sessionId(),
      properties,
      context: {},
    };
    const attemptId = this.deps.identity.attemptId();
    if (attemptId) envelope.quiz_attempt_id = attemptId;
    if (leadId) envelope.lead_id = leadId;
    if (this.deps.config.quizVersion) envelope.quiz_version = this.deps.config.quizVersion;
    if (this.deps.config.interfaceVersion) envelope.interface_version = this.deps.config.interfaceVersion;
    return envelope;
  }
}

export function createAnalytics(deps: AnalyticsDeps): Analytics {
  return new Analytics(deps);
}
